import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axios';
import logo from '../img/tramites.png';
import {
    attachLandingInteractions,
    openGeneralInquiryWhatsApp,
    openServiceWhatsApp
} from '../utils/landingInteractions';
import './Landing.css';
import './LandingWhatsApp.css';

const Landing = () => {
    const [customHtml, setCustomHtml] = useState(null);
    const [loading, setLoading] = useState(true);
    const customLandingRef = useRef(null);

    useEffect(() => {
        const loadCustomLanding = async () => {
            try {
                const response = await axios.get('/landing');
                if (response.data.hasCustomContent && response.data.htmlContent) {
                    setCustomHtml(response.data.htmlContent);
                }
            } catch (error) {
                console.error('Error al cargar landing personalizada:', error);
            } finally {
                setLoading(false);
            }
        };
        loadCustomLanding();
    }, []);

    useEffect(() => {
        if (!customHtml || !customLandingRef.current) return undefined;

        const controller = new AbortController();
        attachLandingInteractions(customLandingRef.current, controller.signal);
        return () => controller.abort();
    }, [customHtml]);

    const handleGeneralInquiry = () => {
        openGeneralInquiryWhatsApp();
    };

    const handleServiceClick = (serviceName) => {
        openServiceWhatsApp(serviceName);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (customHtml) {
        return (
            <>
                <div ref={customLandingRef} dangerouslySetInnerHTML={{ __html: customHtml }} />
                <a 
                    href="https://wa.me/541166371898" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="whatsapp-float"
                    aria-label="Contactar por WhatsApp"
                >
                    <i className="fa-brands fa-whatsapp"></i>
                </a>
            </>
        );
    }

    return (
        <>
            <div className="landing-container">
                {/* Services Section */}
                <section id="features" className="features-section features-section-first">
                    <div className="container">
                        <div className="section-header">
                            <span className="section-badge">Nuestros Servicios</span>
                            <h1 className="section-title">Soluciones Especializadas para tu Negocio</h1>
                            <p className="section-subtitle">Ofrecemos un portafolio completo de servicios diseñados para optimizar tus procesos administrativos y financieros.</p>
                        </div>
                        <div className="features-grid">
                            <div className="feature-card" onClick={() => handleServiceClick('Administración de Pagos')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-money-bill-transfer"></i>
                                </div>
                                <h3>Administración de Pagos</h3>
                                <p>Gestión profesional integral de pagos, depósitos bancarios y rendiciones. Control total y transparencia en cada operación financiera.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Reimpresión de Facturas')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-file-invoice"></i>
                                </div>
                                <h3>Reimpresión de Facturas</h3>
                                <p>Obtención inmediata de comprobantes fiscales. Proceso ágil, seguro y certificado para todas tus necesidades documentales.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Pago de Expensas')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-building"></i>
                                </div>
                                <h3>Pago de Expensas</h3>
                                <p>Gestión completa y automatizada de expensas y rendiciones. Mantené tus pagos al día sin complicaciones ni demoras.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Planes Municipales')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-city"></i>
                                </div>
                                <h3>Planes Municipales</h3>
                                <p>Asesoramiento especializado y gestión eficiente de planes municipales. Facilitamos tus trámites con entidades gubernamentales locales.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Planes ARBA')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-file-contract"></i>
                                </div>
                                <h3>Planes ARBA</h3>
                                <p>Administración eficiente y cumplimiento tributario de planes ARBA. Soluciones adaptadas para evitar complicaciones y demoras.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Pagos AGIP')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-receipt"></i>
                                </div>
                                <h3>Pagos AGIP</h3>
                                <p>Gestión integral de pagos AGIP y servicios tributarios. Soluciones personalizadas adaptadas a las necesidades de tu organización.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Servicios Públicos')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-droplet"></i>
                                </div>
                                <h3>Servicios Públicos</h3>
                                <p>Pagos centralizados de EDEA, OSSE, AySA y más. Una única gestión para todos tus servicios públicos, simplificando tu administración.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Gestoría del Automotor')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-car"></i>
                                </div>
                                <h3>Gestoría del Automotor</h3>
                                <p>Transferencias, prendas, inscripciones 0 km y más. Expertos certificados en trámites vehiculares con resultados garantizados y tiempos optimizados.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Trámites en Registro de la Propiedad Inmueble')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-landmark"></i>
                                </div>
                                <h3>Trámites en Registro de la Propiedad Inmueble</h3>
                                <p>Gestión de trámites ante el Registro de la Propiedad Inmueble. Informe de dominio y demás gestiones registrales con agilidad y seguridad.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                            <div className="feature-card" onClick={() => handleServiceClick('Solicitud de Partidas')}>
                                <div className="feature-icon">
                                    <i className="fa-solid fa-scroll"></i>
                                </div>
                                <h3>Solicitud de Partidas</h3>
                                <p>Obtención de partidas de nacimiento, matrimonio y defunción. Tramitamos tus certificados vitales de forma rápida y sin complicaciones.</p>
                                <div className="feature-link">
                                    <span>Saber más</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* About Section */}
                <section id="about" className="about-section">
                    <div className="about-background-pattern"></div>
                    <div className="container">
                        <div className="about-header">
                            <div className="about-badge">
                                <i className="fa-solid fa-award"></i>
                                <span>Más de 30 años de experiencia</span>
                            </div>
                            <h2 className="about-title">
                                Soluciones Integrales en
                                <span className="about-title-highlight"> Gestión Administrativa</span>
                            </h2>
                            <p className="about-description">
                                Optimizamos tus procesos administrativos con servicios especializados que te permiten enfocarte en lo que realmente importa. Experiencia, eficiencia y compromiso en cada gestión.
                            </p>
                        </div>

                        <div className="about-stats">
                            <div className="about-stat">
                                <div className="about-stat-number">+500</div>
                                <div className="about-stat-label">Clientes satisfechos</div>
                            </div>
                            <div className="about-stat">
                                <div className="about-stat-number">+10K</div>
                                <div className="about-stat-label">Trámites gestionados</div>
                            </div>
                            <div className="about-stat">
                                <div className="about-stat-number">24/7</div>
                                <div className="about-stat-label">Atención disponible</div>
                            </div>
                        </div>

                        <div className="about-body">
                            <div className="about-highlights">
                                <h3 className="about-highlights-title">Servicio Integral</h3>
                                <ul className="about-highlights-list">
                                    <li><i className="fa-solid fa-check"></i>Gestión completa y profesional</li>
                                    <li><i className="fa-solid fa-check"></i>Trámites automotores especializados</li>
                                    <li><i className="fa-solid fa-check"></i>Servicios municipales y tributarios</li>
                                    <li><i className="fa-solid fa-check"></i>Atención personalizada y dedicada</li>
                                    <li><i className="fa-solid fa-star"></i>Certificados y garantizados</li>
                                </ul>
                            </div>
                            <div className="inquiry-box">
                                <div className="inquiry-icon">
                                    <i className="fa-solid fa-message"></i>
                                </div>
                                <h3>¿No encontraste lo que buscabas?</h3>
                                <p>Enviános un mensaje con tu consulta y nuestro equipo te responderá a la brevedad.</p>
                                <button type="button" className="btn-inquiry" onClick={handleGeneralInquiry}>
                                    Consultar
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="process-section">
                    <div className="container">
                        <div className="section-header">
                            <span className="section-badge">Cómo Trabajamos</span>
                            <h2 className="section-title">Proceso Simple y Eficiente</h2>
                            <p className="section-subtitle">Nuestro método probado garantiza resultados rápidos y confiables.</p>
                        </div>
                        <div className="process-steps">
                            <div className="process-step">
                                <div className="step-number">1</div>
                                <div className="step-icon">
                                    <i className="fa-solid fa-comments"></i>
                                </div>
                                <h3>Consulta Inicial</h3>
                                <p>Contactanos y describinos tu necesidad. Nuestro equipo analizará tu caso y te propondrá la mejor solución.</p>
                            </div>
                            <div className="process-step">
                                <div className="step-number">2</div>
                                <div className="step-icon">
                                    <i className="fa-solid fa-file-signature"></i>
                                </div>
                                <h3>Documentación</h3>
                                <p>Te guiamos en la recopilación de documentos necesarios. Proceso simple y transparente.</p>
                            </div>
                            <div className="process-step">
                                <div className="step-number">3</div>
                                <div className="step-icon">
                                    <i className="fa-solid fa-gears"></i>
                                </div>
                                <h3>Gestión</h3>
                                <p>Nuestros expertos procesan tu trámite con eficiencia y seguimiento constante.</p>
                            </div>
                            <div className="process-step">
                                <div className="step-number">4</div>
                                <div className="step-icon">
                                    <i className="fa-solid fa-check-double"></i>
                                </div>
                                <h3>Entrega</h3>
                                <p>Recibís tu trámite completado con documentación y seguimiento post-entrega.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="landing-footer">
                    <div className="container">
                        <div className="footer-content">
                            <div className="footer-brand">
                                <div className="footer-logo">
                                    <img src={logo} alt="Tramites Pronto" />
                                </div>
                                <p className="footer-tagline">Tu aliado confiable en gestión administrativa</p>
                            </div>
                            <div className="footer-info">
                                <h4>Información de Contacto</h4>
                                <div className="footer-contact-item">
                                    <i className="fa-solid fa-envelope"></i>
                                    <span>Tramitespronto02@gmail.com</span>
                                </div>
                                <div className="footer-contact-item">
                                    <i className="fa-solid fa-location-dot"></i>
                                    <span>Moine 1499 (1661) - Bella vista, Pcia. de Bs. As</span>
                                </div>
                                <div className="footer-contact-item">
                                    <i className="fa-brands fa-whatsapp"></i>
                                    <span>+54 11 6637-1898</span>
                                </div>
                            </div>
                        </div>
                        <div className="footer-bottom">
                            <p className="footer-copyright">
                                © {new Date().getFullYear()} Tramites Pronto. Todos los derechos reservados.
                            </p>
                        </div>
                    </div>
                </footer>
            </div>
            <a 
                href="https://wa.me/541166371898" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="whatsapp-float"
                aria-label="Contactar por WhatsApp"
            >
                <i className="fa-brands fa-whatsapp"></i>
            </a>
        </>
    );
};

export default Landing;

